import { createVuetify } from 'vuetify';
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg';
import { icons } from './mdi-icon'; // Import icons from separate file
import {
  PurpleTheme,
  GreenTheme,
  PinkTheme,
  YellowTheme,
  SeaGreenTheme,
  OliveGreenTheme,
  SpeechBlueTheme,
} from '@/theme/LightTheme';
import {
  DarkPurpleTheme,
  DarkGreenTheme,
  DarkSpeechBlueTheme,
  DarkOliveGreenTheme,
  DarkPinkTheme,
  DarkYellowTheme,
  DarkSeaGreenTheme,
} from '@/theme/DarkTheme';
import {
  mdiCalendarMonthOutline,
  mdiCheckboxBlank,
  mdiCheckboxMarked,
  mdiChevronDown,
  mdiChevronRight,
  mdiChevronUp,
  mdiPlusCircleOutline,
} from '@mdi/js';
// Custom defaults
import { VDataTableServer } from 'vuetify/components/VDataTable';
import { VBtn } from 'vuetify/components/VBtn';
import { VSelect } from 'vuetify/components/VSelect';
import { VTextField } from 'vuetify/components/VTextField';
import { VCard } from 'vuetify/components/VCard';
import { VTextarea } from 'vuetify/components/VTextarea';
import { VList } from 'vuetify/components/VList';
import { VTabs } from 'vuetify/components/VTabs';
import { VCombobox } from 'vuetify/components/VCombobox';
import { VAutocomplete } from 'vuetify/components/VAutocomplete';
import { VDateInput } from 'vuetify/labs/components';
import { VCheckbox, VDivider, VSheet } from 'vuetify/components';

// NOTE:
// E stands for 'Eikon'

export const vuetifyConfig = {
  components: {
    VDateInput,
  },
  icons: {
    defaultSet: 'mdi',
    aliases: {
      ...aliases,
      ...icons,
    },
    sets: {
      mdi,
    },
  },
  theme: {
    defaultTheme: 'PurpleTheme',
    variations: {
      colors: ['primary', 'secondary', 'success', 'error', 'warning'],
      lighten: 3,
      darken: 3,
    },
    themes: {
      PurpleTheme,
      GreenTheme,
      PinkTheme,
      YellowTheme,
      SeaGreenTheme,
      OliveGreenTheme,
      SpeechBlueTheme,
      DarkPurpleTheme,
      DarkGreenTheme,
      DarkSpeechBlueTheme,
      DarkOliveGreenTheme,
      DarkPinkTheme,
      DarkYellowTheme,
      DarkSeaGreenTheme,
    },
  },
  // NOTE: Ensure the setupTests.ts createVuetify reads from this object to define its aliases.
  aliases: {
    ETable: VDataTableServer,
    EBtn: VBtn,
    AddBtnCircle: VBtn,
    ECardBtn: VBtn,
    CardActionBtn: VBtn,
    ETextField: VTextField,
    EAutocomplete: VAutocomplete,
    ETextarea: VTextarea,
    ECombobox: VCombobox,
    ESelect: VSelect,
    EDateField: VDateInput,
    LayoutCard: VCard,
    EActionMenu: VList,
    ETabs: VTabs,
    EDivider: VDivider,
    ECheckbox: VCheckbox,
    ESwitchWrap: VSheet,
  },
  defaults: {
    EBtn: {
      class: 'text-body-1',
      variant: 'flat',
      color: 'primary',
    },
    AddBtnCircle: {
      color: 'primary',
      variant: 'text',
      icon: mdiPlusCircleOutline,
    },
    ECardBtn: {
      class: 'text-body-1 font-weight-regular',
      color: 'primary',
      variant: 'text',
      appendIcon: mdiChevronRight,
    },
    CardActionBtn: {
      color: 'primary',
      variant: 'text',
      density: 'compact',
    },
    ESelect: {
      rounded: 'md',
      variant: 'outlined',
      bgColor: 'gray50',
      color: 'primary',
      glow: true,
    },
    ETextField: {
      rounded: 'md',
      variant: 'outlined',
      bgColor: 'gray50',
      color: 'primary',
      glow: true,
    },
    EDateField: {
      rounded: 'md',
      variant: 'outlined',
      bgColor: 'gray50',
      color: 'primary',
      prependIcon: '',
      appendInnerIcon: mdiCalendarMonthOutline,
      glow: true,
      class: 'input-text',
      placeholder: 'mm/dd/yyyy',
    },
    EAutocomplete: {
      rounded: 'md',
      variant: 'outlined',
      bgColor: 'gray50',
      color: 'primary',
      glow: true,
    },
    ETextarea: {
      rounded: 'md',
      variant: 'outlined',
      bgColor: 'gray50',
      color: 'primary',
      glow: true,
    },
    ECombobox: {
      rounded: 'md',
      variant: 'outlined',
      bgColor: 'gray50',
      color: 'primary',
      glow: true,
    },
    EDivider: {
      opacity: 1,
    },
    ECheckbox: {
      falseIcon: mdiCheckboxBlank,
      trueIcon: mdiCheckboxMarked,
      color: 'primary',
    },
    ESwitchWrap: {
      color: 'gray50',
      minHeight: 0,
      class: 'px-4',
      rounded: 'md',
    },
    EActionMenu: {
      density: 'compact',
      color: 'primary',
      slim: true,
    },
    VCard: {
      rounded: 'md',
    },
    LayoutCard: {
      elevation: 0,
      rounded: 'md',
      variant: 'outlined',
      color: 'containerBg',
    },
    VTextField: {
      rounded: 'lg',
    },
    VTooltip: {
      // set v-tooltip default location to top
      location: 'top',
    },
    ETable: {
      color: 'primary',
      class: 'customize-table action-position',
      hideDefaultFooter: true,
      sortAscIcon: mdiChevronUp,
      sortDescIcon: mdiChevronDown,
    },
    ETabs: {
      class: 'pa-4',
      direction: 'vertical',
      color: 'primary',
      hideSlider: true,
      selectedClass: 'active-tab',
      mandatory: true,
    },
    VDataTable: {
      sortAscIcon: mdiChevronUp,
      sortDescIcon: mdiChevronDown,
    },
    VDataTableServer: {
      sortAscIcon: mdiChevronUp,
      sortDescIcon: mdiChevronDown,
    },
    VDataTableVirtual: {
      color: 'primary',
      class: 'customize-table action-position virt-table',
      sortAscIcon: mdiChevronUp,
      sortDescIcon: mdiChevronDown,
    },
  },
};

export default createVuetify(vuetifyConfig);
